/**
 * Utility functions for QuickSight Dashboard with Summary
 */

import { just, nothing, Maybe } from 'maybeasy';
import { loadDateFilter, getDefaultDateRange } from '../../DashboardSummary/utils';

/**
 * Normalize a date string to YYYY-MM-DD format
 * Handles various formats: "2024-01-15", "2024-01-15T00:00:00Z", etc.
 */
const normalizeDateString = (dateStr: string): string => {
  if (!dateStr) return dateStr;

  // If already in YYYY-MM-DD format (10 chars), return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // Try to extract YYYY-MM-DD from the beginning of the string
  const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) {
    return match[1];
  }

  // Fallback: try parsing as Date and formatting
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }

  return dateStr;
};

/**
 * Extract QuickSight dashboard ID from the embed URL
 */
export const extractDashboardId = (dashboardUri: string): string | null => {
  if (!dashboardUri) {
    return null;
  }

  try {
    // QuickSight embed URLs contain the dashboard ID as a path segment
    // Format: https://...quicksight.../embed/DASHBOARD_ID or in query params
    const url = new URL(dashboardUri);
    const pathParts = url.pathname.split('/');
    const dashboardIdIndex = pathParts.indexOf('dashboards') + 1;
    if (dashboardIdIndex > 0 && dashboardIdIndex < pathParts.length) {
      return pathParts[dashboardIdIndex];
    }
    // Also try query param
    return url.searchParams.get('dashboardId') || null;
  } catch {
    // If URL parsing fails, assume dashboardUri is already the dashboard ID
    return dashboardUri;
  }
};

/**
 * Extract filter values from QuickSight parameters
 * Falls back to saved date filter from Home page, then to defaults
 * @param parameters - QuickSight dashboard parameters
 * @param useFallback - If true, use saved/default dates when QuickSight params unavailable
 */
export const extractFiltersFromParameters = (
  parameters: Record<string, string>,
  useFallback: boolean = false,
): Maybe<Record<string, string>> => {
  const startDateRaw =
    parameters['pDateStart'] || parameters['StartDate'] || parameters['start_date'];
  const endDateRaw = parameters['pDateEnd'] || parameters['EndDate'] || parameters['end_date'];

  // If QuickSight hasn't provided date parameters yet
  if (!startDateRaw || !endDateRaw) {
    // Try to use saved date filter from Home page (for Home -> Reports transition)
    if (useFallback) {
      const savedFilter = loadDateFilter();
      if (savedFilter) {
        return just({
          date_type: 'registration',
          start_date: savedFilter.startDate,
          end_date: savedFilter.endDate,
        });
      }
      // Fall back to defaults
      const defaults = getDefaultDateRange();
      return just({
        date_type: 'registration',
        start_date: defaults.startDate,
        end_date: defaults.endDate,
      });
    }
    return nothing();
  }

  const filters = {
    date_type: (
      parameters['pDateSelection'] ||
      parameters['DateType'] ||
      parameters['date_type'] ||
      'registration'
    ).toLowerCase(),
    start_date: normalizeDateString(startDateRaw),
    end_date: normalizeDateString(endDateRaw),
  };

  // Only include non-empty filter values
  const cleanedFilters: Record<string, string> = {};
  if (filters.date_type) cleanedFilters.date_type = filters.date_type;
  if (filters.start_date) cleanedFilters.start_date = filters.start_date;
  if (filters.end_date) cleanedFilters.end_date = filters.end_date;

  return just(cleanedFilters);
};

/**
 * Check if filters have changed
 */
export const filtersHaveChanged = (
  oldFilters: Record<string, string>,
  newFilters: Record<string, string>,
): boolean => {
  const relevantKeys = ['date_type', 'start_date', 'end_date'];
  return relevantKeys.some((key) => oldFilters[key] !== newFilters[key]);
};

/**
 * Check if a dashboard has an Action Plan sheet
 */
export const checkForActionPlanSheet = async (dashboard: any): Promise<boolean> => {
  if (!dashboard?.getSheets) {
    console.warn('[checkForActionPlanSheet] Dashboard does not have getSheets method');
    return false;
  }

  try {
    const sheets: Array<{ Name: string; SheetId: string }> = await dashboard.getSheets();
    return sheets.some(
      (sheet) =>
        sheet.Name?.toLowerCase().includes('action plan') ||
        sheet.Name?.toLowerCase().includes('action_plan'),
    );
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('[checkForActionPlanSheet] Failed to get sheets:', errorMessage);
    return false;
  }
};
